import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useLiveSubscription, useScopeLive } from '../../contexts/LiveEventsContext'
import { useDynamicRefresh } from '../../hooks/useDynamicRefresh'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { EmptyState } from '../ui/empty-state'
import { Card } from '../ui/card'
import { mdiAlertCircleOutline, mdiArchiveOutline, mdiClose, mdiBackburger, mdiCellphone, mdiChevronDown, mdiClockOutline, mdiEmailOutline, mdiForwardburger, mdiInboxOutline, mdiLayersOutline, mdiLoading, mdiMagnify, mdiMessageOutline, mdiPaperclip, mdiPencilOutline } from '@mdi/js'
import { ConversationPanel } from './ConversationPanel'
import { BrowserNotificationsToggle } from './BrowserNotificationsToggle'
import { NewConversationButton } from './NewConversationDialog'
import type { Conversation, ConversationStatus } from '../../api/schema'
import { PRIORITY_KEYS, PRIORITY_TONES, threadPriority, threadTitleT } from './thread'

/**
 * The request queue, laid out like a mail client because that is what a
 * manager already knows how to read: folders on the left (the thread's state,
 * its channel, the ones that need a human), the list in the middle (who, what,
 * when, the first line), the thread on the right.
 *
 * It is still not a mail client underneath. A conversation is a request whose
 * channel happens to be e-mail today and WhatsApp tomorrow, and reading it
 * beside the tickets it produced is the whole point of putting it in the app
 * instead of leaving it in a mailbox — which is why the thread pane carries
 * the assets, the tickets and the context, and a mailbox does not.
 */

const DEFAULT_STATUS = 'open,waiting_resident'

const FOLDERS = [
    { key: 'inbox', status: DEFAULT_STATUS, icon: mdiInboxOutline },
    { key: 'waiting', status: 'waiting_resident', icon: mdiClockOutline },
    { key: 'closed', status: 'closed', icon: mdiArchiveOutline },
    { key: 'all', status: 'open,waiting_resident,closed', icon: mdiLayersOutline },
] as const

const CHANNELS = ['email', 'web', 'whatsapp'] as const

const CHANNEL_ICON = {
    email: mdiEmailOutline,
    whatsapp: mdiCellphone,
    web: mdiMessageOutline,
} as const

/** Sender states that mean "a human still owes this thread a decision". */
const NEEDS_ATTENTION = new Set(['unresolved', 'probabilistic'])

/**
 * Whether the queue strip is folded away while a thread is open.
 *
 * Per-viewer convenience, so it lives in `localStorage` and NOT in the URL: a
 * shared `?conversation=` link must open the same conversation for everyone, and
 * "I like my queue hidden" is not part of what is being shared.
 */
const QUEUE_COLLAPSED_KEY = 'conversations.queueCollapsed'

function readQueueCollapsed(): boolean {
    try {
        return localStorage.getItem(QUEUE_COLLAPSED_KEY) === 'true'
    } catch {
        return false
    }
}

export function ConversationsPage() {
    const { org } = useParams({ from: '/$org/conversations' })
    const search = useSearch({ from: '/$org/conversations' })
    const navigate = useNavigate({ from: '/$org/conversations' })
    const { t, i18n } = useTranslation('conversations')

    const status = search.status ?? DEFAULT_STATUS
    const conversationUid = search.conversation ?? null
    const focusedMessage = search.message ?? null

    const [queueCollapsed, setQueueCollapsed] = useState<boolean>(readQueueCollapsed)
    useEffect(() => {
        try {
            localStorage.setItem(QUEUE_COLLAPSED_KEY, String(queueCollapsed))
        } catch {
            /* a viewer who blocks storage still gets the layout, just not the memory */
        }
    }, [queueCollapsed])

    const setSearch = (next: Record<string, string | undefined>) => {
        navigate({
            search: (prev: Record<string, unknown>) => ({ ...prev, ...next }) as never,
            replace: true,
        })
    }

    // `?selected=` is the pre-2026-09-09 name for the same address. A
    // bookmark or a link pasted in a chat history still opens: redirect it to
    // `conversation` once, on load, rather than reading it forever — this
    // fallback is for one release only.
    const legacySelected = search.selected
    useEffect(() => {
        if (!legacySelected || search.conversation) return
        setSearch({ conversation: legacySelected, selected: undefined })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [legacySelected, search.conversation])

    const unreadOnly = (search as { unread?: string }).unread === 'true'
    const list = $api.useQuery('get', '/api/v1/organizations/{orgCode}/conversations', {
        params: {
            path: { orgCode: org },
            query: {
                status,
                channel: search.channel || undefined,
                resolution: search.resolution || undefined,
                asset: search.asset || undefined,
                unattached: search.unattached === 'true' ? true : undefined,
                // The sidebar badge's list (spec 2026-10-27-23).
                unread: unreadOnly ? true : undefined,
                draft: search.draft === 'true' ? true : undefined,
                q: search.q || undefined,
                limit: 50,
            },
        },
    })
    const counters = $api.useQuery('get', '/api/v1/organizations/{orgCode}/settings/conversations', {
        params: { path: { orgCode: org } },
    })
    // The drafts toggle's own badge count (resolved open question: no
    // dedicated counters-strip entry, a count on the pill is enough). Not
    // scoped to `status` — it answers "how many drafts do I have anywhere",
    // which is the reason to reach for the toggle in the first place, not
    // just within whatever folder happens to be open. `limit: 1` because only
    // `total` is read.
    const draftsCount = $api.useQuery('get', '/api/v1/organizations/{orgCode}/conversations', {
        params: {
            path: { orgCode: org },
            query: { draft: true, limit: 1 },
        },
    })

    // Live: an inbound message must land in the open queue without a manual
    // refresh (spec 2026-09-08-03). The collection scope covers every thread —
    // a child row's hint dirties it too — and the queue answers by re-fetching
    // through the scoped API, so a scoped-grant manager still sees only what
    // they may read.
    useLiveSubscription({ entity: 'conversations' })
    const isQueueLive = useScopeLive({ entity: 'conversations' })
    // The polling safety net underneath: pinned to a lazy 5 minutes while the
    // socket is acked, back to adaptive backoff the moment it drops. See
    // docs/frontend/dynamic-refresh.md.
    const queueRefresh = useDynamicRefresh({ defaultInterval: 15000, live: isQueueLive })
    const refetchList = list.refetch
    const refetchCounters = counters.refetch
    const refetchDraftsCount = draftsCount.refetch
    useEffect(() => {
        if (queueRefresh.timeRemaining !== 0) return
        void refetchList()
        void refetchCounters()
        void refetchDraftsCount()
    }, [queueRefresh.timeRemaining, refetchList, refetchCounters, refetchDraftsCount])

    const rows = useMemo(() => (list.data?.data ?? []) as Conversation[], [list.data])

    // When a status change (close / reopen) makes the selected thread leave
    // the folder on screen, advance to the thread that was next in the queue
    // — the same gesture every mail client applies on archive (spec
    // 2026-09-10-06). `rows` is the snapshot held at the moment of the
    // callback, not after the invalidation the panel already queued: by the
    // time that refetch lands, the row is gone.
    const handleStatusChanged = (next: ConversationStatus) => {
        if (status.split(',').includes(next)) return // stays in the folder (e.g. "All"): nothing moves
        const index = rows.findIndex((row) => row.uid === conversationUid)
        if (index === -1) return // selected by URL, not in this queue: stay put
        const successor = rows[index + 1] ?? rows[index - 1]
        // Same call as ConversationRow.onSelect — opens the successor like a
        // click does, so the breadcrumb and read acknowledgement follow it;
        // no successor clears `conversation`, landing on the empty state.
        setSearch({ conversation: successor?.uid, message: undefined })
    }

    // The open thread as the panel itself loaded it. Same query key as the
    // panel's, so TanStack Query serves it from the one cache entry and no
    // second request is made — it is only here so the breadcrumb can name a
    // thread the QUEUE does not hold. That is no longer an edge case: a thread
    // just opened from "write to a contact" carries no message yet, and is
    // filtered out of the queue until it does (spec 2026-09-16-10), so reading
    // the leaf off `rows` alone named every freshly started thread "(no
    // subject)".
    const openThread = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/conversations/{conversationUid}',
        { params: { path: { orgCode: org, conversationUid: conversationUid ?? '' } } },
        { enabled: Boolean(conversationUid) },
    )

    // Selecting a thread is a route change (`?conversation=`), so the breadcrumb
    // follows it: the queue becomes a link that keeps the current filters,
    // and the thread's subject is the leaf. The subject comes off the row
    // already loaded for the queue, falling back to the thread the panel
    // loaded. The "back to the queue" link never carries
    // `conversation`/`message`/legacy `selected` — those address a thread, not
    // a filter.
    const breadcrumbs = useMemo(() => {
        const home = { title: t('common:nav.home'), path: `/${org}` }
        if (!conversationUid) return [home, { title: t('title') }]
        const { conversation: _omitConversation, message: _omitMessage, selected: _omitSelected, reply_for_issue: _omitReplyForIssue, ...filters } = search
        const query = new URLSearchParams(
            Object.entries(filters).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1] !== ''),
        ).toString()
        const row = rows.find((row) => row.uid === conversationUid) ?? (openThread.data as Conversation | undefined)
        return [
            home,
            { title: t('title'), path: `/${org}/conversations${query ? `?${query}` : ''}` },
            { title: threadTitleT(row, t) },
        ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversationUid, rows, openThread.data, org, t, JSON.stringify(search)])
    useBreadcrumbs(breadcrumbs)

    const folders = (
        <FoldersRail
            status={status}
            channel={search.channel}
            unattached={search.unattached === 'true'}
            draft={search.draft === 'true'}
            draftsCount={draftsCount.data?.total ?? 0}
            onStatus={(value) =>
                setSearch({ status: value === DEFAULT_STATUS ? undefined : value, conversation: undefined, message: undefined })
            }
            onChannel={(value) => setSearch({ channel: value, conversation: undefined, message: undefined })}
            onUnattached={() => setSearch({ unattached: search.unattached === 'true' ? undefined : 'true' })}
            onDraft={() => setSearch({ draft: search.draft === 'true' ? undefined : 'true' })}
        />
    )

    // Reading mode. Browsing (no thread open) is the queue's own screen and
    // keeps the three columns; reading gives the room to the thread and its
    // context, because that is what the operator is looking at.
    const reading = Boolean(conversationUid)
    const queueStrip = reading && !queueCollapsed
    const columns = !reading
        ? 'lg:grid-cols-[10rem_minmax(0,19rem)_minmax(0,1fr)] xl:grid-cols-[11rem_minmax(0,22rem)_minmax(0,1fr)] 2xl:grid-cols-[12rem_minmax(0,26rem)_minmax(0,1fr)]'
        : queueCollapsed
          ? 'lg:grid-cols-[2.5rem_minmax(0,1fr)]'
          : 'lg:grid-cols-[minmax(0,16rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,18rem)_minmax(0,1fr)]'

    return (
        <div
            className="flex flex-col gap-4 lg:h-[calc(100dvh-5rem)] lg:min-h-0"
            data-testid="conversations-page"
            data-mode={reading ? 'reading' : 'browsing'}
        >
            {/* One line while reading: the chrome above the thread says nothing
                about the thread. */}
            {reading ? (
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-baseline gap-4 min-w-0">
                        <h1 className="text-lg font-semibold text-foreground flex-shrink-0">{t('title')}</h1>
                        <CountersStrip counters={counters.data} inline />
                    </div>
                    {/* Managing where mail can arrive is configuration, and
                        configuration lives in a page — not in a modal holding an
                        unbounded list (spec 2026-09-09-13). */}
                    <div className="flex items-center gap-2">
                        <BrowserNotificationsToggle size="sm" />
                        <Button variant="outline" size="sm" asChild data-testid="conversations-endpoints-button">
                            <Link to="/$org/conversations/intake-addresses" params={{ org }}>
                                <AppIcon path={mdiEmailOutline} size="xs" className="md:mr-1" />
                                <span className="hidden md:inline">{t('actions.endpoints')}</span>
                            </Link>
                        </Button>
                    </div>
                </div>
            ) : (
                <PageHeader
                    icon={SECTION_ICONS.conversations}
                    title={t('title')}
                    description={t('subtitle')}
                    actions={
                        <div className="flex items-center gap-2">
                            <BrowserNotificationsToggle />
                            <Button variant="outline" asChild data-testid="conversations-endpoints-button">
                                <Link to="/$org/conversations/intake-addresses" params={{ org }}>
                                    <AppIcon path={mdiEmailOutline} size="xs" className="md:mr-1" />
                                    <span className="hidden md:inline">{t('actions.endpoints')}</span>
                                </Link>
                            </Button>
                            {/* The ledger's one outbound-first door (spec
                                2026-09-15-01): write to a contact, and the answer
                                threads back here instead of into a mailbox. */}
                            <NewConversationButton org={org} />
                        </div>
                    }
                />
            )}

            {!reading && <CountersStrip counters={counters.data} />}

            <Card
                className={`overflow-hidden grid grid-cols-1 content-start lg:divide-x min-h-[70vh] lg:min-h-0 lg:flex-1 ${columns}`}
                data-testid="conversations-client"
            >
                {/* Folders: a rail on wide screens while browsing. Reading folds
                    them into the list header — the rail's 10-12rem is width the
                    thread wants more than the queue does. */}
                {!reading && <div className="block">{folders}</div>}

                {/* The queue. On a phone the thread replaces it; on a laptop both
                    stay, narrowed to a strip while a thread is open. */}
                {reading && queueCollapsed ? (
                    <div className="hidden lg:flex flex-col items-center pt-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setQueueCollapsed(false)}
                            aria-expanded={false}
                            aria-controls="conversations-queue"
                            aria-label={t('queue.expand')}
                            title={t('queue.expand')}
                            data-testid="conversations-toggle-queue"
                        >
                            <AppIcon path={mdiForwardburger} size="sm" />
                        </Button>
                    </div>
                ) : (
                    <div className={`flex flex-col min-h-0 ${reading ? 'hidden lg:flex' : 'flex'}`}>
                        <div className="p-2 border-b space-y-2">
                            {/* Reading: the folder + channel filters live behind
                                one chip instead of a column of their own. */}
                            {reading && (
                                <div className="flex items-center gap-1">
                                    <FoldersMenu
                                        status={status}
                                        channel={search.channel}
                                        unattached={search.unattached === 'true'}
                                        draft={search.draft === 'true'}
                                    >
                                        {folders}
                                    </FoldersMenu>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 flex-shrink-0"
                                        onClick={() => setQueueCollapsed(true)}
                                        aria-expanded={true}
                                        aria-controls="conversations-queue"
                                        aria-label={t('queue.collapse')}
                                        title={t('queue.collapse')}
                                        data-testid="conversations-toggle-queue"
                                    >
                                        <AppIcon path={mdiBackburger} size="sm" />
                                    </Button>
                                </div>
                            )}
                            {unreadOnly && (
                                <span
                                    className="inline-flex items-center gap-1.5 rounded-full border border-destructive/30 bg-destructive/5 px-2.5 py-1 text-xs"
                                    data-testid="conversations-unread-chip"
                                >
                                    {t('queue.unread_only')}
                                    <button
                                        type="button"
                                        onClick={() => setSearch({ unread: undefined })}
                                        aria-label={t('queue.unread_clear')}
                                        title={t('queue.unread_clear')}
                                        className="text-muted-foreground hover:text-foreground"
                                        data-testid="conversations-unread-chip-clear"
                                    >
                                        <AppIcon path={mdiClose} size="xs" />
                                    </button>
                                </span>
                            )}
                            <div className="relative">
                                <AppIcon path={mdiMagnify} size="xs" className="absolute left-2.5 top-1/2 -translate-y-1/2" />
                                <Input
                                    value={search.q ?? ''}
                                    onChange={(e) => setSearch({ q: e.target.value || undefined })}
                                    placeholder={t('queue.search_placeholder')}
                                    className="pl-8 h-8"
                                    data-testid="conversations-search"
                                />
                            </div>
                        </div>
                        <div id="conversations-queue" className="flex-1 overflow-y-auto divide-y" data-testid="conversations-queue">
                            {list.isLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <AppIcon path={mdiLoading} size="lg" className="motion-safe:animate-spin" />
                                </div>
                            ) : rows.length === 0 ? (
                                <EmptyState
                                    variant="inline"
                                    live
                                    icon={SECTION_ICONS.conversations}
                                    title={t('queue.empty')}
                                    data-testid="conversations-queue-empty"
                                />
                            ) : (
                                rows.map((row) => (
                                    <ConversationRow
                                        key={row.uid}
                                        row={row}
                                        selected={conversationUid === row.uid}
                                        locale={i18n.language}
                                        compact={queueStrip}
                                        onSelect={() => setSearch({ conversation: row.uid, message: undefined })}
                                    />
                                ))
                            )}
                        </div>
                        <div className="px-3 py-1.5 border-t text-[11px] text-muted-foreground">
                            {t('queue.total', { count: Number(list.data?.total ?? rows.length) })}
                        </div>
                    </div>
                )}

                {/* The thread. `@container` is what makes the panel's own
                    container queries resolve against THIS column rather than
                    nothing — it is the width of the pane, not of the window,
                    that decides whether the details column fits. */}
                <div className={`min-h-0 overflow-y-auto @container ${conversationUid ? 'flex flex-col' : 'hidden lg:flex lg:flex-col'}`}>
                    {conversationUid ? (
                        <ConversationPanel
                            org={org}
                            uid={conversationUid}
                            focusedMessage={focusedMessage}
                            replyForIssue={search.reply_for_issue ?? null}
                            onReplyForIssueHandled={() => setSearch({ reply_for_issue: undefined })}
                            onFocusMessage={(messageUid) => setSearch({ message: messageUid ?? undefined })}
                            onClose={() => setSearch({ conversation: undefined, message: undefined })}
                            onStatusChanged={handleStatusChanged}
                        />
                    ) : (
                        <EmptyState
                            className="flex-1 justify-center"
                            icon={mdiInboxOutline}
                            title={t('panel.select_prompt')}
                            data-testid="conversation-empty-reading"
                        />
                    )}
                </div>
            </Card>
        </div>
    )
}

/**
 * The folders rail, folded into one chip.
 *
 * Same component behind it — the rail is not re-implemented for the popover,
 * so a folder added to `FOLDERS` shows up in both placements at once.
 */
function FoldersMenu({
    status,
    channel,
    unattached,
    draft,
    children,
}: {
    status: string
    channel?: string
    unattached: boolean
    draft: boolean
    children: React.ReactNode
}) {
    const { t } = useTranslation('conversations')
    const folder = FOLDERS.find((entry) => entry.status === status)
    const folderIcon = folder?.icon ?? mdiInboxOutline

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    size="sm"
                    className="h-8 flex-1 min-w-0 justify-start gap-1.5 px-2 text-xs"
                    aria-label={t('folders.label')}
                    title={t('folders.label')}
                    data-testid="conversations-folder-menu"
                >
                    <AppIcon path={folderIcon} size="xs" className="flex-shrink-0" />
                    <span className="truncate">{folder ? t(`folders.${folder.key}`) : t('folders.all')}</span>
                    {channel && <span className="truncate text-muted-foreground">· {t(`channel.${channel}`)}</span>}
                    {unattached && <AppIcon path={mdiAlertCircleOutline} size="xs" className="flex-shrink-0 text-amber-600 dark:text-amber-400" />}
                    {draft && <AppIcon path={mdiPencilOutline} size="xs" className="flex-shrink-0 text-muted-foreground" />}
                    <AppIcon path={mdiChevronDown} size="xs" tone="muted" className="ml-auto flex-shrink-0 opacity-60" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-56 p-0" data-testid="conversations-folder-menu-content">
                {children}
            </PopoverContent>
        </Popover>
    )
}

function FoldersRail({
    status,
    channel,
    unattached,
    draft,
    draftsCount,
    onStatus,
    onChannel,
    onUnattached,
    onDraft,
}: {
    status: string
    channel?: string
    unattached: boolean
    draft: boolean
    draftsCount: number
    onStatus: (value: string) => void
    onChannel: (value: string | undefined) => void
    onUnattached: () => void
    onDraft: () => void
}) {
    const { t } = useTranslation('conversations')

    const item = (active: boolean, extra = '') =>
        `flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-left whitespace-nowrap transition-colors ${
            active ? 'bg-primary/10 text-foreground font-medium' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
        } ${extra}`

    return (
        <nav className="flex lg:flex-col gap-1 p-2 overflow-x-auto lg:overflow-visible" aria-label={t('folders.label')} data-testid="conversations-folders">
            {FOLDERS.map((folder) => {
                const folderIcon = folder.icon
                const active = status === folder.status
                return (
                    <button
                        key={folder.key}
                        type="button"
                        className={item(active)}
                        onClick={() => onStatus(folder.status)}
                        aria-current={active ? 'page' : undefined}
                        data-testid={`conversations-folder-${folder.key}`}
                    >
                        <AppIcon path={folderIcon} size="sm" className="flex-shrink-0" />
                        <span>{t(`folders.${folder.key}`)}</span>
                    </button>
                )
            })}

            <div className="hidden lg:block px-2.5 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {t('folders.channels')}
            </div>
            <button
                type="button"
                className={item(!channel, 'lg:pl-2.5')}
                onClick={() => onChannel(undefined)}
                data-testid="conversations-channel-all"
            >
                <AppIcon path={mdiLayersOutline} size="sm" className="flex-shrink-0 lg:hidden" />
                <span>{t('channel.all')}</span>
            </button>
            {CHANNELS.map((code) => {
                const channelIcon = CHANNEL_ICON[code]
                return (
                    <button
                        key={code}
                        type="button"
                        className={item(channel === code)}
                        onClick={() => onChannel(channel === code ? undefined : code)}
                        data-testid={`conversations-channel-${code}`}
                    >
                        <AppIcon path={channelIcon} size="sm" className="flex-shrink-0" />
                        <span>{t(`channel.${code}`)}</span>
                    </button>
                )
            })}

            <div className="hidden lg:block pt-3" />
            {/* The unattached queue is a filter, not a page: its remedy is one
                click on the very same panel. */}
            <button
                type="button"
                className={item(unattached)}
                onClick={onUnattached}
                aria-pressed={unattached}
                data-testid="conversations-unattached-toggle"
            >
                <AppIcon path={mdiAlertCircleOutline} size="sm" className="flex-shrink-0 text-amber-600 dark:text-amber-400" />
                <span>{t('folders.attention')}</span>
            </button>
            {/* The way back to a thread with an unsent draft (spec
                2026-09-23-03): a toggle next to `unattached`, same shape —
                the remedy is the same panel, one click away. Composes with
                `status`, per the spec's resolved open questions. */}
            <button
                type="button"
                className={`${item(draft)} justify-between`}
                onClick={onDraft}
                aria-pressed={draft}
                data-testid="conversations-draft-toggle"
            >
                <span className="flex items-center gap-2">
                    <AppIcon path={mdiPencilOutline} size="sm" className="flex-shrink-0" />
                    <span>{t('folders.drafts')}</span>
                </span>
                {draftsCount > 0 && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1.5 flex-shrink-0" data-testid="conversations-draft-count">
                        {draftsCount}
                    </Badge>
                )}
            </button>
        </nav>
    )
}

function ConversationRow({
    row,
    selected,
    locale,
    compact = false,
    onSelect,
}: {
    row: Conversation
    selected: boolean
    locale: string
    /** The narrow reading strip: who and what, nothing else. */
    compact?: boolean
    onSelect: () => void
}) {
    const { t } = useTranslation('conversations')
    const rowChannelIcon = CHANNEL_ICON[(row.channel ?? 'email') as keyof typeof CHANNEL_ICON] ?? mdiEmailOutline
    const sender = displaySender(row, t('contact.anonymous'))
    const unread = (row.unread_count ?? 0) > 0
    const needsAttention = NEEDS_ATTENTION.has(row.contact_resolution ?? '') || !row.asset
    const preview = (row as { preview?: string }).preview ?? ''
    const priority = threadPriority(row)

    const open = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelect()
        }
    }
    const rowClass = `w-full text-left flex items-start gap-3 cursor-pointer transition-colors border-l-2 ${
        selected ? 'bg-primary/10 border-l-primary' : 'border-l-transparent hover:bg-muted/50'
    }`

    // A 16rem strip is not a mail list: the date, the channel and the preview
    // are all things the thread beside it already says. Who wrote, and about
    // what, is what still has to be scannable.
    if (compact) {
        return (
            <div
                role="button"
                tabIndex={0}
                onClick={onSelect}
                onKeyDown={open}
                className={`${rowClass} px-2.5 py-2 items-center`}
                data-testid={`conversation-row-${row.uid}`}
            >
                <div
                    className={`h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-medium flex-shrink-0 ${
                        unread ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                    }`}
                    aria-hidden
                >
                    {initialsOf(sender)}
                </div>
                <div className="min-w-0 flex-1">
                    {/* `truncate` rather than `line-clamp-1`: an address is one
                        unbreakable token, and a line clamp would cut it
                        mid-glyph with no ellipsis to say it was cut. */}
                    <div className={`text-xs truncate ${unread ? 'font-semibold text-foreground' : 'text-foreground'}`}>{sender}</div>
                    <div className={`text-xs line-clamp-1 ${unread ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                        {threadTitleT(row, t)}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={open}
            className={`${rowClass} px-3 py-2.5`}
            data-testid={`conversation-row-${row.uid}`}
        >
            <div
                className={`h-9 w-9 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5 ${
                    unread ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                }`}
                aria-hidden
            >
                {initialsOf(sender)}
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2 min-w-0">
                    <span className={`text-sm truncate ${unread ? 'font-semibold text-foreground' : 'text-foreground'}`}>{sender}</span>
                    <span className="text-[11px] text-muted-foreground flex-shrink-0 inline-flex items-center gap-1">
                        <AppIcon path={rowChannelIcon} size="xs" aria-label={t(`channel.${row.channel ?? 'email'}`)} />
                        {formatQueueDate(row.last_inbound_at ?? row.created_at, locale, t('queue.today'))}
                    </span>
                </div>
                <div className="flex items-center gap-1.5 min-w-0">
                    {/* Only the levels that change what a manager does next
                        (spec 2026-09-23-04): a dot on every Medium row would
                        be noise, and the thread header states any level. */}
                    {priority <= 2 && (
                        <span
                            className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_TONES[priority]}`}
                            role="img"
                            aria-label={t('priority.row', { level: t(`priority.${PRIORITY_KEYS[priority]}`) })}
                            title={t('priority.row', { level: t(`priority.${PRIORITY_KEYS[priority]}`) })}
                            data-testid="conversation-row-priority"
                            data-priority={priority}
                        />
                    )}
                    <span className={`text-sm truncate ${unread ? 'font-medium text-foreground' : 'text-foreground/90'}`}>
                        {threadTitleT(row, t)}
                    </span>
                </div>
                <div className="flex items-start gap-2 min-w-0 mt-0.5">
                    {/* Two lines, not one: the preview is the thread's
                        generated summary when it has one (spec 2026-09-09-07),
                        and a summary says what is asked AND where it stands —
                        which does not fit on one truncated line. A thread
                        still on the first-line fallback simply never reaches
                        the second. */}
                    <span className="text-xs text-muted-foreground line-clamp-2 flex-1 min-w-0" data-testid="conversation-row-preview">
                        {preview || (row.asset?.name ? String(row.asset.name) : '')}
                    </span>
                    {unread && (
                        <Badge
                            variant="default"
                            className="text-[10px] h-4 px-1.5 flex-shrink-0"
                            data-testid="conversation-row-unread-badge"
                        >
                            {row.unread_count}
                        </Badge>
                    )}
                    {needsAttention && (
                        <AppIcon path={mdiAlertCircleOutline} size="xs" className="text-amber-600 dark:text-amber-400 flex-shrink-0" aria-label={t('resolution.unresolved')} />
                    )}
                    {(row.linked_issues ?? []).length > 0 && (
                        <AppIcon path={mdiPaperclip} size="xs" className="flex-shrink-0" aria-hidden />
                    )}
                </div>
            </div>
        </div>
    )
}

/**
 * The realized counters, published rather than buried in a settings page.
 *
 * "Answered without a ticket" and the sender-identification split are the two
 * numbers that say whether the ledger is working: the first is the share the
 * referential answered, the second tells a syndic how reachable its own
 * occupants actually are.
 */
function CountersStrip({ counters, inline = false }: { counters?: Record<string, unknown>; inline?: boolean }) {
    const { t } = useTranslation('conversations')
    if (!counters) return null

    const num = (key: string) => Number(counters[key] ?? 0)
    const median = counters.first_response_median_hours as number | null | undefined
    const resolution = counters.resolution_median_days as number | null | undefined
    const reopened = counters.reopened_rate as number | null | undefined
    const nps = counters.nps as number | null | undefined
    const npsResponses = num('nps_responses')

    return (
        <div
            className={
                inline
                    ? // One line, always: the point of reading mode is that the
                      // chrome above the thread stops growing. From `xl` the
                      // counters ride beside the title; narrower than that the
                      // title and the intake button already own the row, so
                      // they are dropped rather than wrapped onto a second one.
                      // Past `xl` the labels ellipsize — all six numbers stay
                      // on the row, no second row is ever made.
                      'hidden xl:flex flex-nowrap items-baseline gap-x-4 overflow-hidden text-xs text-muted-foreground min-w-0'
                    : 'flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground'
            }
            data-testid="conversations-counters"
        >
            <Counter label={t('counters.volume')} value={String(num('volume'))} testId="counter-volume" inline={inline} />
            <Counter
                label={t('counters.resolved_without_ticket')}
                value={String(num('resolved_without_ticket'))}
                testId="counter-without-ticket"
                inline={inline}
            />
            <Counter
                label={t('counters.first_response')}
                value={median == null ? '—' : t('counters.hours', { value: median.toFixed(1) })}
                testId="counter-first-response"
                inline={inline}
            />
            {/* The three lot-2 facts (spec 2026-09-11-01): resolution, premature
                closures, and the NPS as measured — with its n, because at 40
                answers ±15 points is not a signal. */}
            <Counter
                label={t('counters.resolution')}
                value={resolution == null ? '—' : t('counters.days', { value: resolution.toFixed(1) })}
                testId="counter-resolution"
                inline={inline}
            />
            <Counter
                label={t('counters.reopened')}
                value={reopened == null ? '—' : `${Math.round(reopened * 100)} %`}
                testId="counter-reopened"
                inline={inline}
            />
            <Counter
                label={nps == null ? t('counters.nps') : `${t('counters.nps')} · ${t('counters.nps_responses', { count: npsResponses })}`}
                value={nps == null ? '—' : `${nps > 0 ? '+' : ''}${Math.round(nps)}`}
                testId="counter-nps"
                inline={inline}
            />
            <Counter label={t('counters.stage_a')} value={String(num('resolved_at_stage_a'))} testId="counter-stage-a" inline={inline} />
            <Counter
                label={t('counters.after_human')}
                value={String(num('resolved_after_human'))}
                testId="counter-after-human"
                inline={inline}
            />
            <Counter label={t('counters.unattached')} value={String(num('unattached'))} testId="counter-unattached" inline={inline} />
        </div>
    )
}

function Counter({ label, value, testId, inline = false }: { label: string; value: string; testId: string; inline?: boolean }) {
    return (
        <div className="flex items-baseline gap-1.5 min-w-0" data-testid={testId}>
            <span
                className={`text-foreground tabular-nums flex-shrink-0 ${inline ? 'text-xs font-medium' : 'text-sm font-semibold'}`}
            >
                {value}
            </span>
            {inline && (
                <span className="flex-shrink-0" aria-hidden>
                    ·
                </span>
            )}
            {/* Inline, the row is what it is: the labels give way before the
                numbers do, so six counters ellipsize rather than the sixth one
                being cut in half by the container's clip. */}
            <span className={inline ? 'truncate' : undefined}>{label}</span>
        </div>
    )
}

/**
 * An anonymous declaration carries a per-submission identity token so two
 * residents scanning the same label do not share a thread. That token is
 * plumbing, not a name — printing it in the queue would be worse than printing
 * nothing.
 */
export function displaySender(
    conversation: { contact?: { name?: string | null } | null; channel_identity?: string | null },
    anonymousLabel: string,
): string {
    const name = conversation.contact?.name
    if (name) return name
    const identity = conversation.channel_identity ?? ''

    return identity.startsWith('anonymous:') ? anonymousLabel : identity
}

/** Up to two initials for an avatar: "Jean Dupont" → "JD", "x@y.z" → "X". */
export function initialsOf(name: string): string {
    const words = name
        .replace(/<.*>/, '')
        .split(/[\s@._-]+/)
        .filter(Boolean)
    if (words.length === 0) return '?'
    if (words.length === 1) return words[0].slice(0, 1).toUpperCase()

    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

/** Mail-client date: the time today, the day this year, the full date before. */
export function formatQueueDate(value: string | null | undefined, locale: string, todayLabel: string): string {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    const now = new Date()
    const sameDay = date.toDateString() === now.toDateString()
    if (sameDay) {
        return `${todayLabel} ${date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`.trim()
    }
    if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' })
    }

    return date.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })
}

export function formatDate(value: string | null | undefined, locale: string): string {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''

    return date.toLocaleDateString(locale, { day: '2-digit', month: 'short' })
}
